// journeys/index.ts — six authored reading paths through KTP.
// Each journey throttles a first-time reader: curated sequence first,
// full site opens after completion.

export type UserType =
  | "parent"
  | "policymaker"
  | "researcher"
  | "technical"
  | "leadership"
  | "skeptic"
  | "community"
  | "casual";

export type JourneyStop = {
  id: string;
  href: string;
  label: string;
  hook: string;
  /** Core question this stop answers — shown in the diagram. */
  question: string;
  /** Estimated reading time for this stop. */
  stopMinutes: number;
};

export type ConnectedJourney = {
  id: string;
  /** One sentence: why would someone cross to this path? */
  direction: string;
};

export type AuthoredJourney = {
  id: string;
  userType: UserType;
  title: string;
  tagline: string;
  estimatedMins: number;
  stops: JourneyStop[];
  /** What to say to the next person — the spread prompt. */
  remarkabilityPrompt: string;
  /** Where to send them — the page worth sharing. */
  remarkabilityHref: string;
  /** Three things you'll leave this path able to do or say. */
  outcomes: string[];
  /** Two adjacent paths worth knowing about. */
  connectedJourneys: ConnectedJourney[];
  /** Stakes — why this persona should care right now. 2–3 sentences. */
  whyItMatters: string;
  /** Value prop — what KTP specifically gives this persona. 2–3 sentences. */
  whatIsThePoint: string;
};

export const JOURNEYS: AuthoredJourney[] = [
  {
    id: "parent",
    userType: "parent",
    title: "For Parents",
    tagline:
      "Your child's attention environment is being designed. Here's how to weigh in.",
    estimatedMins: 12,
    remarkabilityPrompt:
      "Tell another parent: \"There is a forkable school board resolution — drafted for adaptation using existing policy precedents — that takes 15 minutes to hand to a board member. I can send it to you.\"",
    remarkabilityHref: "/advocacy/templates",
    outcomes: [
      "Name the specific economic interests that profit from keeping phones in schools",
      "Hand a school board member a tested, forkable resolution before the next meeting",
      "Tell another parent exactly what to say — and which page to send them to",
    ],
    connectedJourneys: [
      {
        id: "policymaker",
        direction: "Want to move from your child's school to the policy level?",
      },
      {
        id: "community-leaders",
        direction: "Connecting with other parents and community organizers already moving?",
      },
    ],
    whyItMatters:
      "Your child's attention environment is not neutral — it is designed by people whose business models depend on capturing that attention as early and as often as possible. Whether phones are in your school is a substrate question: what conditions for human development are actually available in that room.",
    whatIsThePoint:
      "This path gives you the adversary map, tested legislation language, and Tuesday-morning templates. You leave with something specific to do before the next school board meeting — not urgency without a target.",
    stops: [
      {
        id: "journey:parent:phone-free-zones",
        href: "/advocacy/k12/phone-free-zones",
        label: "Phone-Free Zones",
        question: "Is my child's attention environment being actively designed against them?",
        stopMinutes: 8,
        hook: "Seven states have already enacted this. See the adversary map — who profits from the current design — and why bell-to-bell is the only version that works.",
      },
      {
        id: "journey:parent:templates",
        href: "/advocacy/templates",
        label: "Tuesday Morning Templates",
        question: "What can I do before the next school board meeting?",
        stopMinutes: 3,
        hook: "A forkable school board resolution ready to hand to a board member before the next meeting. Adapt it to your district — don't use it verbatim.",
      },
      {
        id: "journey:parent:scoreboard",
        href: "/advocacy/scoreboard",
        label: "Advocacy Snapshot",
        question: "Is bell-to-bell working anywhere yet?",
        stopMinutes: 1,
        hook: "A dated snapshot of earlier policy precedents, kept separate from recorded KTP activity and measured outcomes. Empty cells are honest cells.",
      },
    ],
  },
  {
    id: "policymaker",
    userType: "policymaker",
    title: "For Policy Makers",
    tagline:
      "The intervention menu, outcome metrics, and the coalition already moving.",
    estimatedMins: 18,
    remarkabilityPrompt:
      "Tell a colleague: \"I found a framework that lists its own falsification conditions up front — it tells you exactly what would prove it wrong. The intervention menu has enforcement architecture built in. Worth 20 minutes of your time.\"",
    remarkabilityHref: "/advocacy/playbook",
    outcomes: [
      "Distinguish interventions with enforcement architecture from those that rely on goodwill",
      "Explain why current measurement regimes make ineffective interventions look successful",
      "Name the organizations already aligned — and what they have specifically committed to",
    ],
    connectedJourneys: [
      {
        id: "researcher",
        direction: "Want the academic backing for these interventions?",
      },
      {
        id: "parent",
        direction: "Want to hear directly from the families this policy affects?",
      },
    ],
    whyItMatters:
      "Most interventions fail not because of bad intent but because they lack enforcement architecture. Without it, policies rely on goodwill and measure the wrong outputs — making ineffective programs look successful on paper.",
    whatIsThePoint:
      "KTP is an intervention catalog with falsification conditions built in. Every intervention states what would prove it wrong. That discipline separates analysis from advocacy — and makes the menu useful to people who need to justify what they fund.",
    stops: [
      {
        id: "journey:policymaker:playbook",
        href: "/advocacy/playbook",
        label: "Intervention Playbook",
        question: "Which interventions have enforcement architecture — not just intent?",
        stopMinutes: 7,
        hook: "Five substrate-level interventions, each with enforcement architecture, equity vectors, and falsification conditions. This is the menu.",
      },
      {
        id: "journey:policymaker:measurement-reform",
        href: "/advocacy/k12/measurement-reform",
        label: "Measurement Reform",
        question: "Why do current metrics make bad interventions look good?",
        stopMinutes: 5,
        hook: "What gets measured shapes what gets funded. The current metrics miss the substrate entirely. Here's what to add — and why legislators keep missing it.",
      },
      {
        id: "journey:policymaker:kpis",
        href: "/advocacy/kpis",
        label: "Outcome KPIs",
        question: "How will I know if an intervention actually worked?",
        stopMinutes: 4,
        hook: "The leading and lagging indicators that tell you whether an intervention worked — before and after adoption.",
      },
      {
        id: "journey:policymaker:coalition",
        href: "/advocacy/coalition",
        label: "Coalition Map",
        question: "Who is already moving — and what have they committed to?",
        stopMinutes: 2,
        hook: "Who is already aligned, what they have committed to, and where the gaps are.",
      },
    ],
  },
  {
    id: "researcher",
    userType: "researcher",
    title: "For Researchers",
    tagline: "The physics, the falsification conditions, and the empirical cases.",
    estimatedMins: 29,
    remarkabilityPrompt:
      "Tell another researcher: \"There is a public trust-model overview, a register of proposed tests, and explicit limits. The register distinguishes test definitions from completed results. I want to know which test you would run first.\"",
    remarkabilityHref: "/research/falsification",
    outcomes: [
      "Explain the public trust model, its substrate limit, and the scope of the private instrument",
      "Distinguish proposed falsifiers, case interpretations, and completed empirical results",
      "Trace the framework from its public model overview to proposed interventions and recorded outcomes",
    ],
    connectedJourneys: [
      {
        id: "technical",
        direction: "Want to see the formal Blue Zone specification?",
      },
      {
        id: "skeptic",
        direction: "Want the framework stress-tested against its hardest objections?",
      },
    ],
    whyItMatters:
      "Trust is routinely modeled as a dependent variable — an outcome of other conditions. There is a case to be made that it operates as a physical constraint on what systems can achieve. That reframing has consequences for how institutions, technology, and human behavior are modeled.",
    whatIsThePoint:
      "This path introduces the public Trust Force model, its limits, and proposed tests. The full instrumented form remains in private research. Use the register to evaluate the test designs and identify the evidence still needed.",
    stops: [
      {
        id: "journey:researcher:research-hub",
        href: "/research",
        label: "Research Hub",
        question: "What is the full scope of the research agenda?",
        stopMinutes: 3,
        hook: "The research landscape in one map: cartography, cognitive artifacts, cosmologies, the Shipwreck cases, the falsification surface. This is the territory before the territory.",
      },
      {
        id: "journey:researcher:trust-force-equation",
        href: "/learn/trust-force-equation",
        label: "The Trust Force Equation",
        question: "What does the public model claim about trust?",
        stopMinutes: 8,
        hook: "A plain-language overview of availability, capacity, witnessed cost, co-presence, reciprocity, and the shadow of the future. The full instrumented form remains in private research.",
      },
      {
        id: "journey:researcher:simulator",
        href: "/learn/simulator",
        label: "P⁴ Simulator",
        question: "What does the conceptual presence simulator illustrate?",
        stopMinutes: 4,
        hook: "Adjust the four presence dimensions in a conceptual teaching model. The simulator illustrates the framing; it does not validate the private Trust Force instrument.",
      },
      {
        id: "journey:researcher:falsification",
        href: "/research/falsification",
        label: "Falsification Conditions",
        question: "What empirical conditions would disprove this framework?",
        stopMinutes: 6,
        hook: "Read the proposed empirical tests, their competing explanations, and their status. Active entries are published test definitions; the register does not yet record completed resolutions.",
      },
      {
        id: "journey:researcher:shipwreck",
        href: "/research/shipwreck",
        label: "Agents of Chaos",
        question: "What real cases test the model at its hardest limits?",
        stopMinutes: 5,
        hook: "Five cases from an external study interpreted through the framework, including a positive collaboration case. These are retrospective comparisons, not an independent validation of KTP.",
      },
      {
        id: "journey:researcher:advocacy",
        href: "/advocacy",
        label: "Advocacy Application",
        question: "Where does the theory become operational?",
        stopMinutes: 3,
        hook: "Proposed interventions, reusable templates, and a dated advocacy ledger. Adoption and measured outcomes are separate from the framework's empirical validity.",
      },
    ],
  },
  {
    id: "technical",
    userType: "technical",
    title: "For Technical Readers",
    tagline:
      "The public model overview, control plane architecture, risk surface, and formal specification.",
    estimatedMins: 29,
    remarkabilityPrompt:
      "Tell a systems architect: \"There is a formal spec for a trust-safe AI operating environment and a risk coverage analysis of 1,612 MIT AI scenarios scored against it. The motion risk framing is worth your disagreement.\"",
    remarkabilityHref: "/governed-autonomy/architecture",
    outcomes: [
      "Evaluate the public model's assumptions and identify the implementation evidence still needed",
      "Map the 7-primitive control plane onto your existing enterprise infrastructure",
      "Assess whether the Blue Zone specification can be implemented in your system",
    ],
    connectedJourneys: [
      {
        id: "leadership",
        direction: "Want the organizational case for what you'd be implementing?",
      },
      {
        id: "researcher",
        direction: "Want the academic anchoring behind the architecture?",
      },
    ],
    whyItMatters:
      "AI systems can act at the motion layer while governance instruments still operate at the policy layer. The gap between what your systems can do and what your leadership can observe is not a configuration problem — it is structural, and existing security frameworks were not designed to close it.",
    whatIsThePoint:
      "KTP frames trust as a control plane, not a culture initiative. The 7-primitive architecture, tool gateway pattern, and least-trajectory principle give systems architects a formal specification — backed by a risk coverage analysis of 1,612 MIT AI scenarios scored against it.",
    stops: [
      {
        id: "journey:technical:trust-force-equation",
        href: "/learn/trust-force-equation",
        label: "The Trust Force Equation",
        question: "What can the public overview establish, and what remains private?",
        stopMinutes: 8,
        hook: "The named relational conditions, substrate limits, and model boundaries. The full instrumented form remains private, so this page supports conceptual review rather than independent mathematical verification.",
      },
      {
        id: "journey:technical:simulator",
        href: "/learn/simulator",
        label: "P⁴ Simulator",
        question: "How do the four presence dimensions actually compose in practice?",
        stopMinutes: 4,
        hook: "Interact with the P⁴ decomposition before mapping it to infrastructure. Adjust proximity, attention, creation, and witness — and see how the six systems in the Commons section held together where others collapsed.",
      },
      {
        id: "journey:technical:architecture",
        href: "/governed-autonomy/architecture",
        label: "System Architecture",
        question: "How does this map onto enterprise infrastructure?",
        stopMinutes: 5,
        hook: "How KTP maps onto enterprise infrastructure. The 7-primitive control plane, tool gateway pattern, governability ladder L0-L6, and least-trajectory principle for AI motion risk.",
      },
      {
        id: "journey:technical:risks",
        href: "/governed-autonomy/risks",
        label: "AI Risk Scorecard",
        question: "What does 1,612-scenario empirical coverage actually look like?",
        stopMinutes: 5,
        hook: "1,612 MIT AI risks scored against KTP's 9 primitives. Mean coverage 41%. Privacy at 100% Boundary. AI Safety motion-dominant. 348 explicit 'None' — the overclaims we refused.",
      },
      {
        id: "journey:technical:blue-zones",
        href: "/specs/blue-zones",
        label: "Blue Zone Specification",
        question: "What is the formal spec for a trust-safe operating environment?",
        stopMinutes: 2,
        hook: "The formal spec for a trust-safe operating environment. What conditions must hold before motion is authorized. The technical counterpart to the policy playbook.",
      },
      {
        id: "journey:technical:learn",
        href: "/learn",
        label: "Concept Map",
        question: "How does all of this fit together in the broader conceptual ecosystem?",
        stopMinutes: 5,
        hook: "The full conceptual landscape: every layer from first principles to implementation, with the trust-force equation at the center. Now that you've seen the architecture, the risk surface, and the spec — see how the pieces connect.",
      },
    ],
  },
  {
    id: "leadership",
    userType: "leadership",
    title: "For Technology Leaders",
    tagline:
      "The control plane argument, the architecture, and the motion risk surface — for the team that will be asked to build it.",
    estimatedMins: 20,
    remarkabilityPrompt:
      "Tell a board member or a direct report: \"I spent 20 minutes on something that reframes AI trust as a control plane problem, not a culture initiative. There is a skeptical reader's guide written for people who push back hard. What time can we talk?\"",
    remarkabilityHref: "/governed-autonomy",
    outcomes: [
      "Frame the AI governance conversation as a control plane problem — not a culture initiative — in terms a skeptical board will hear",
      "Walk through the eight strongest technical objections before they arrive in your team's review",
      "Map the 7-primitive KTP architecture onto your existing enterprise infrastructure",
    ],
    connectedJourneys: [
      {
        id: "technical",
        direction: "Ready to go deeper on the formal specification and architecture?",
      },
      {
        id: "researcher",
        direction: "Want the academic anchoring behind what you'd be implementing?",
      },
    ],
    whyItMatters:
      "Organizations are deploying AI at the motion layer — where action happens — while governance instruments still operate at the policy layer. What your systems can do and what your leadership can see are diverging faster than most governance instruments can track. This is not a management gap. It is a structural one.",
    whatIsThePoint:
      "KTP frames AI governance as substrate provisioning, not rule-setting. This path gives you the board-level case, the eight strongest objections handled directly, and the architectural spec for what a trust-safe operating environment looks like — concrete enough to evaluate, formal enough to implement.",
    stops: [
      {
        id: "journey:leadership:about",
        href: "/about",
        label: "What This Is",
        question: "What exactly is this project and what does it claim?",
        stopMinutes: 3,
        hook: "The founding claim: the Internet was built to maximize motion, not to protect the substrate that makes motion worth anything. This page names the project and discloses what it is not.",
      },
      {
        id: "journey:leadership:enterprise",
        href: "/governed-autonomy",
        label: "Enterprise Overview",
        question: "What is the board-level case for treating trust as a control plane?",
        stopMinutes: 6,
        hook: "Trust as control plane, not culture initiative. Three risk buckets, four deployment phases, CISO value props. The frame before your board asks the question.",
      },
      {
        id: "journey:leadership:skeptical",
        href: "/governed-autonomy/skeptical",
        label: "Skeptical Reader's Guide",
        question: "What are the eight strongest objections — and are they handled?",
        stopMinutes: 5,
        hook: "Eight objections handled directly: tight definitions, the bounded public claim, the ladder from intuition to evidence. Written for the technical leader who arrived to find the contradiction.",
      },
      {
        id: "journey:leadership:architecture",
        href: "/governed-autonomy/architecture",
        label: "System Architecture",
        question: "What would we actually be building?",
        stopMinutes: 6,
        hook: "The 7-primitive control plane, tool gateway pattern, governability ladder L0-L6, and least-trajectory principle. The map of what a trust-safe operating environment requires to implement.",
      },
    ],
  },
  {
    id: "skeptic",
    userType: "skeptic",
    title: "For Skeptics",
    tagline:
      "Start with what we can't prove. We wrote the failure modes so you didn't have to find them.",
    estimatedMins: 16,
    remarkabilityPrompt:
      "Tell another skeptic: \"They wrote the failure modes before critics arrived. The falsification conditions are on the first screen. I still have questions — but I want to hear your objections first. Come look at what they say they cannot claim.\"",
    remarkabilityHref: "/canon/limits",
    outcomes: [
      "Name the framework's published limitations and the claims it has revised",
      "Read the falsification register and distinguish proposed tests from completed results",
      "Find what the framework says it cannot claim — before arriving at those limits yourself",
    ],
    connectedJourneys: [
      {
        id: "researcher",
        direction: "Satisfied with the failure modes? Here's the full theory.",
      },
      {
        id: "technical",
        direction: "Want to evaluate the formal specification for yourself?",
      },
    ],
    whyItMatters:
      "Any trust framework faces three structural risks: selecting examples that confirm rather than test, reframing failures as convergences, and substituting compelling vocabulary for mechanism. These are not minor quibbles — they determine whether this is a theory or a dressed intuition.",
    whatIsThePoint:
      "The failure modes were written before you arrived. The falsification conditions are on the first screen, not the last. This path starts with what the framework cannot claim and works outward — if that's still not enough, the Skeptical Reader's Guide handles eight objections directly.",
    stops: [
      {
        id: "journey:skeptic:limits",
        href: "/canon/limits",
        label: "What We Can't Claim",
        question: "What has the framework explicitly said it cannot prove?",
        stopMinutes: 4,
        hook: "The canon of known limits: where the framework breaks, what it doesn't cover, what remains contested. We wrote this before skeptics arrived so the omissions are visible, not buried.",
      },
      {
        id: "journey:skeptic:falsification",
        href: "/research/falsification",
        label: "Falsification Conditions",
        question: "What empirical conditions would break this model?",
        stopMinutes: 5,
        hook: "The public register lists test definitions, competing explanations, and revision criteria. It has no recorded empirical resolutions; use it to assess the proposed tests.",
      },
      {
        id: "journey:skeptic:enterprise-skeptical",
        href: "/governed-autonomy/skeptical",
        label: "The Skeptical Reader's Guide",
        question: "What are the eight strongest objections — and are they handled?",
        stopMinutes: 5,
        hook: "Eight objections handled directly: tight definitions, the bounded public claim, the ladder from intuition to evidence. Written for the reader who arrived to find the contradiction.",
      },
      {
        id: "journey:skeptic:scoreboard",
        href: "/advocacy/scoreboard",
        label: "Advocacy Snapshot",
        question: "What adoption and outcome evidence has the advocacy program recorded?",
        stopMinutes: 2,
        hook: "The May 7, 2026 advocacy snapshot separates earlier policy precedents from KTP-associated adoption and measured outcomes. Empty entries remain visible; this is not a completed empirical test of the theory.",
      },
    ],
  },
  {
    id: "community-leaders",
    userType: "community",
    title: "For Community Leaders",
    tagline:
      "You want to change the system, not just navigate it. The advocacy catalog is built for the Tuesday-morning organizer.",
    estimatedMins: 14,
    remarkabilityPrompt:
      "Tell another organizer: \"The advocacy templates are forkable — you don't start from scratch. There's a draft school board resolution and a coalition map distinguishing commitments from potential partners. Let me send you the page.\"",
    remarkabilityHref: "/advocacy/templates",
    outcomes: [
      "Explain phone-free schools as a substrate argument — not a technology preference — in terms any school board or congregation can hear",
      "Hand a fellow organizer a tested intervention template ready for the next meeting",
      "Name the existing coalition and what each member has specifically committed to do",
    ],
    connectedJourneys: [
      {
        id: "parent",
        direction: "Want to hear from the families most directly affected?",
      },
      {
        id: "policymaker",
        direction: "Ready to move from community organizing to legislative action?",
      },
    ],
    whyItMatters:
      "The systems shaping human development were not built to serve human development. They were built — and are actively maintained — by interests that profit from the current arrangement. Community leadership at the substrate level means changing what the default is, not organizing within it.",
    whatIsThePoint:
      "KTP's advocacy catalog is built specifically for the Tuesday-morning organizer who has 15 minutes before a school board meeting or a congregational gathering. The interventions are forkable, the templates are ready to hand to a board member, and the coalition is tiered so you know who's actually committed versus who's adjacent.",
    stops: [
      {
        id: "journey:community-leaders:advocacy",
        href: "/advocacy",
        label: "Advocacy Hub",
        question: "What is this intervention catalog and how is it structured?",
        stopMinutes: 3,
        hook: "The entry point: five substrate-level interventions with enforcement architecture, equity vectors, and falsification conditions. The catalog that exists because the alternatives are position papers without standing.",
      },
      {
        id: "journey:community-leaders:playbook",
        href: "/advocacy/playbook",
        label: "Intervention Playbook",
        question: "Why is this approach different from standard policy advocacy?",
        stopMinutes: 4,
        hook: "The methodology behind the catalog: two-surface schema, eight discipline rules, falsification conditions built in. Every claim has a falsifier. Every template acknowledges the failure mode it will meet on first contact.",
      },
      {
        id: "journey:community-leaders:phone-free-zones",
        href: "/advocacy/k12/phone-free-zones",
        label: "Phone-Free Zones",
        question: "What does a substrate-level intervention actually look like?",
        stopMinutes: 5,
        hook: "The lead intervention in full: seven states, adversary map, bell-to-bell enforcement architecture, equity audit, and the Tuesday-morning bundle ready to hand to a board member.",
      },
      {
        id: "journey:community-leaders:coalition",
        href: "/advocacy/coalition",
        label: "Coalition Map",
        question: "Who is already moving — and what have they actually committed to?",
        stopMinutes: 2,
        hook: "Who's aligned, what they've committed to, and where the gaps are. Tiered: allied, wobbly, should-support-doesn't. Assumed alignment excluded.",
      },
    ],
  },
  {
    id: "casual",
    userType: "casual",
    title: "For Curious Readers",
    tagline:
      "You found this without looking for it. Start with the prose — the physics reveals itself.",
    estimatedMins: 15,
    remarkabilityPrompt:
      "Tell someone: \"I found a site where an 800-year-old water system is used to diagnose the internet. I'm still not sure what it is exactly. You should look at it.\"",
    remarkabilityHref: "/writing",
    outcomes: [
      "Find one essay you want to send to someone without explaining it first",
      "Name the case from Agents of Chaos that stayed with you — and say why it fits",
      "Discover what an acequia has to do with the internet",
    ],
    connectedJourneys: [
      {
        id: "researcher",
        direction: "Found yourself wanting the full theoretical structure?",
      },
      {
        id: "skeptic",
        direction: "Want to look for the holes before going deeper?",
      },
    ],
    whyItMatters:
      "Most frameworks arrive as frameworks — they announce the theory before they've earned your attention. This one grew from observation and language before it became physics. If that order matters to you, the prose is the right door.",
    whatIsThePoint:
      "No prerequisites. No required reading. The essays are standalone. The cases are concrete. The Acequia is either going to mean something to you or it isn't. If it does, the rest of the site will feel different.",
    stops: [
      {
        id: "journey:casual:writing",
        href: "/writing",
        label: "The Essays",
        question: "What is the prose that came before the physics?",
        stopMinutes: 4,
        hook: "The writing that built the framework's vocabulary before the framework had equations. Essays, observations, and the language the physics eventually grew into.",
      },
      {
        id: "journey:casual:shipwreck",
        href: "/research/shipwreck",
        label: "Agents of Chaos",
        question: "What does it look like when a trust system collapses?",
        stopMinutes: 6,
        hook: "Five cases from the Agents of Chaos study, including failures and a positive collaboration, interpreted through the framework. Compare the interpretation with the original study and its limits.",
      },
      {
        id: "journey:casual:acequia",
        href: "/physics/acequia",
        label: "The Acequia",
        question: "What does an 800-year-old water system have to do with the internet?",
        stopMinutes: 4,
        hook: "An acequia is a community-managed irrigation channel that has run in the American Southwest for centuries without a corporation or a government mandate. There is a reason it is in this framework.",
      },
      {
        id: "journey:casual:about",
        href: "/about",
        label: "What This Is",
        question: "After all that — what is this, really?",
        stopMinutes: 3,
        hook: "A one-page answer. What the project claims, what it doesn't, and why a water system and a collapse case and a physics equation ended up on the same site.",
      },
    ],
  },
];

export function getJourney(id: string): AuthoredJourney | undefined {
  return JOURNEYS.find((j) => j.id === id);
}

export function getJourneyByType(userType: UserType): AuthoredJourney | undefined {
  return JOURNEYS.find((j) => j.userType === userType);
}
